"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Hero() {
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const stats = [{
    value: "15+",
    label: "YEARS EXP"
  }, {
    value: "200+",
    label: "HOTELS"
  }, {
    value: "100%",
    label: "DATA-DRIVEN"
  }];
  return <section data-section-id="432" id="casestudyhero" className="bg-background text-foreground relative overflow-hidden min-h-[720px] h-screen w-full">
      {}
      <div ref={imageRef} data-index={0} className={`absolute inset-0 lg:left-[40%] lg:right-0 transition-all duration-[1400ms] ease-out ${imageVisible ? "opacity-100 scale-100" : "opacity-0 scale-105"}`}>
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_strategy.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_strategy.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_strategy.png-wvc-sizes" alt="Professional hotel revenue management dashboard and analytics" className="w-full h-full object-cover" loading="eager" decoding="async" />
        {}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_40%,hsl(222_47%_7%/0.6)_100%)]" />
        {}
        <div className="hidden lg:block absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-background via-background/80 to-transparent" />
        {}
        <div className="lg:hidden absolute inset-0 bg-background/75" />
      </div>

      {}
      <div className="relative z-10 h-full w-full max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        <div className="h-full grid lg:grid-cols-5 items-center">
          {}
          <div ref={contentRef} className={`lg:col-span-2 flex flex-col justify-center pt-24 lg:pt-0 transition-all duration-1000 ease-out ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            {}
            <div className="flex items-center gap-3 mb-8 font-mono text-xs sm:text-sm tracking-widest uppercase">
              <span className="text-secondary">●</span>
              <span className="text-secondary">HOTELREVBABA</span>
              <span className="text-muted-foreground">/</span>
              <span className="text-foreground/80">REVENUE CONSULTANCY</span>
            </div>

            {}
            <h1 className="font-default font-serif font-light tracking-tight leading-[0.95] text-foreground mb-8" style={{
            fontSize: "clamp(3.5rem, 7vw, 7rem)"
          }}>
              Maximize your{" "}
              <span className="relative inline-block italic font-medium">
                revenue
                <span className="absolute left-0 right-0 bottom-[0.15em] h-[2px] bg-secondary" />
              </span>
              <span className="text-secondary">.</span>
            </h1>

            {}
            <p className="text-base sm:text-lg text-muted-foreground max-w-xl mb-6 leading-relaxed font-sans">
              Expert revenue management strategies for hotel owners and investors. We leverage data-driven OTA insights to boost your direct bookings and profitability.
            </p>

            <div className="flex flex-col gap-3 mb-10">
              <Link to="#" className="text-sm font-medium text-secondary hover:underline flex items-center gap-2">
                Explore Our Services <ArrowRight className="size-3" />
              </Link>
              <Link to="/FreeHotelRevenueAuditNoCostCompleteAnalysisPage" className="text-sm font-medium text-secondary hover:underline flex items-center gap-2">
                Get Your Free Audit <ArrowRight className="size-3" />
              </Link>
            </div>

            {}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 mb-16 lg:mb-20">
              <Button asChild size="lg" className="bg-secondary text-primary-foreground hover:bg-secondary/90 font-medium px-7 h-12 text-sm tracking-wide rounded-lg">
                <Link to="/ContactPage">Book a Consultation <ArrowRight className="ml-1 size-4" /></Link>
              </Button>
              <Button asChild variant="link" className="text-foreground hover:text-secondary px-0 h-auto text-sm tracking-wide underline-offset-8 hover:underline">
                <Link to="#">View Our Services</Link>
              </Button>
            </div>
          </div>

          {}
          <div className="hidden lg:block lg:col-span-3" />
        </div>

        {}
        <div className={`absolute bottom-8 sm:bottom-10 left-6 sm:left-10 lg:left-16 right-6 sm:right-auto z-10 transition-all duration-1000 delay-300 ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="flex flex-wrap items-center gap-x-4 sm:gap-x-6 gap-y-2 font-mono text-[11px] sm:text-xs tracking-widest uppercase">
            {stats.map((stat, i) => <div data-index={i} key={i} className="flex items-center gap-4 sm:gap-6">
                {i > 0 && <span className="text-secondary">●</span>}
                <div className="flex items-baseline gap-2">
                  <span className="text-foreground font-medium">{stat.value}</span>
                  <span className="text-muted-foreground">{stat.label}</span>
                </div>
              </div>)}
          </div>
        </div>

        {}
        <div className="hidden md:flex absolute bottom-8 right-6 lg:right-16 z-10 flex-col items-center gap-3">
          <span className="font-mono text-[10px] tracking-[0.3em] text-muted-foreground uppercase" style={{
          writingMode: "vertical-rl"
        }}>
            Scroll
          </span>
          <div className="relative w-px h-16 bg-border overflow-hidden">
            <span className="absolute top-0 left-0 w-full h-6 bg-secondary animate-[scrollLine_2s_ease-in-out_infinite]" />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes scrollLine {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(400%); }
        }
      `}</style>
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [{
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://hotelrevbaba.com/"
        }, {
          "@type": "ListItem",
          "position": 2,
          "name": "5-Star Resort Revenue Optimization",
          "item": "https://hotelrevbaba.com/CaseStudy5StarResortIndiaRevenueOptimizationPage"
        }]
      })
    }} />
    </section>;
}